import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Favicon } from '../../resources/assets/images/favicons';
import { useBrand } from '../../providers/BrandProvider';

const FOOD_AMIGOS_ICONS = [
  { src: 'favicon.ico', sizes: '64x64 32x32 24x24 16x16', type: 'image/x-icon' },
  { src: 'logo192.png', sizes: '192x192', type: 'image/png', purpose: 'maskable' },
  { src: 'logo512.png', sizes: '512x512', type: 'image/png' },
];

// Brand-colored dot favicon for white-label brands without an uploaded logo,
// so a neutral/logo-less host never falls back to the FoodAmigos icon.
function buildFallbackIcon(primaryColor) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><circle cx="16" cy="16" r="16" fill="${primaryColor}"/></svg>`;

  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

export default function MetaInfo() {
  const brand = useBrand();

  const isWhiteLabel = brand?.isWhiteLabel;
  const brandIcon = isWhiteLabel ? (brand.circleLogoUrl ?? brand.logoUrl) : null;
  const icon = isWhiteLabel ? (brandIcon ?? buildFallbackIcon(brand.primaryColor)) : Favicon;
  const title = isWhiteLabel ? brand.name : 'FoodAmigos Orders Portal';

  // react-helmet's <title> does not reliably win over the static index.html
  // title under React 19, so the tab title is set imperatively. The static
  // shell only carries neutral placeholders (see index.html).
  useEffect(() => {
    document.title = title;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', brand.primaryColor);
  }, [title, brand?.primaryColor]);

  useEffect(() => {
    const manifest = {
      name: title,
      short_name: isWhiteLabel ? brand.name : 'FoodAmigos',
      start_url: '/',
      scope: '.',
      display: 'standalone',
      theme_color: brand.primaryColor,
      background_color: '#ffffff',
      icons: isWhiteLabel
        ? brandIcon
          ? [{ src: brandIcon, sizes: '512x512', type: 'image/png' }]
          : []
        : FOOD_AMIGOS_ICONS,
    };

    const url = URL.createObjectURL(new Blob([JSON.stringify(manifest)], { type: 'application/json' }));
    document.querySelector('link[rel="manifest"]')?.setAttribute('href', url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [isWhiteLabel, title, brand?.name, brand?.primaryColor, brandIcon]);

  return (
    <div className="application">
      <Helmet>
        <meta charSet="utf-8" />
        <link rel="icon" id="favicon" href={icon} />
        <meta property="og:image" content={icon} />
        <meta name="twitter:image" content={icon} />
      </Helmet>
    </div>
  );
}
